import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { getQuoteAppliedDocuments } from 'lib/api';
import { getCompanyConfig } from 'lib/api/company';
import { getReplacementInvoices } from 'lib/api/factoring';
import {
  downloadFilesFromRequest,
  uploadFilesFromRequest,
} from 'lib/api/files';
import DocumentsPreviewer, {
  useDocumentPreview,
} from 'lib/common/DocumentsPreviewer';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import RequirementsDialog from 'lib/designSystem/RequirementsDialog';
import EnhancedTable from 'lib/designSystem/Table';
import Uploader from 'lib/designSystem/Uploader';
import { showAlert, validatePermissions } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import PayersConcentration from './components/PayersConcentration';
import { ReplacementDialog } from './components/ReplacementDialog';
import { appliedHeaders, documentssHeaders } from './tableHeaders';

const DocumentsDetailsModule = (props: any) => {
  const { selectedRequest, setSelectedRequest, setOpenShoppingCart, setUp } =
    props;
  const [selected, setSelected] = useState<string[]>([]);
  const [tableInView, setTableInView] = useState('documents');
  const [loading, setLoading] = useState(false);
  const [documentsApplied, setDocumentsApplied] = useState<any[]>([]);
  const [openUploadDocumentPDF, setOpenUploadDocumentPDF] = useState(false);
  const [file, setFile] = useState<any>(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [documentToUpload, setDocumentToUpload] = useState('');
  const [replacementInvoices, setReplacementInvoices] = useState<any[]>([]);
  const [openSelectReplacementInvoice, setOpenSelectReplacementInvoice] =
    useState(false);
  const [openRequirementsDialog, setOpenRequirementsDialog] = useState(false);
  const [documentId, setDocumentId] = useState<string>('');
  const [documentAlias, setDocumentAlias] = useState<string>('');
  const [documentToReplace, setDocumentToReplace] = useState<any>(null);
  const [replacementEnabled, setReplacementEnabled] = useState<boolean>(false);
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const dispatch = useDispatch();

  const {
    previewOpen,
    previewLoading,
    previewError,
    currentPreviewFile,
    blobUrl,
    contentType,
    handleClosePreview,
    setPreviewError,
  } = useDocumentPreview({
    requestId: selectedRequest?.id || '',
    requestType: 'document',
  });

  const handleDocumentsApplied = async () => {
    setLoading(true);
    try {
      const documentResponse = await getQuoteAppliedDocuments(dispatch, {
        quoteId: selectedRequest.quoteActive.id,
      });
      setDocumentsApplied(documentResponse);
      setTableInView('applied');
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleDocuments = () => {
    setTableInView('documents');
  };

  const actionButtons = [
    {
      name: 'Documentos',
      action: () => handleDocuments(),
      color: 'secondary',
      disabled: tableInView === 'documents',
    },
    {
      name: '% Deudores',
      action: () => setTableInView('payers'),
      color: 'secondary',
      disabled: tableInView === 'payers',
    },
    {
      name: 'Aplicados',
      action: () => handleDocumentsApplied(),
      color: 'secondary',
      disabled: tableInView === 'applied' || !selectedRequest.quoteActive,
    },
  ];

  const handleFileChange = (event: any) => {
    const uploadedFile = event.target.files[0];
    setUploadedFileName(uploadedFile.name);
    setFile(uploadedFile);
  };

  const uploadFiles = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      // Change the file Name to name prop
      const fileToUpload = new File([file], `${documentToUpload}.pdf`, {
        type: file.type,
      });
      formData.append('files', fileToUpload);
      await uploadFilesFromRequest(dispatch, formData, {
        requestId: documentToUpload,
        requestType: 'document',
      });
      showAlert(dispatch, 'success', 'Se han subido tus archivos con éxito');
      setDocumentToUpload('');
      const documents = selectedRequest.documents.map((document: any) => {
        if (
          `${document.businessIdentifier}-${document.folio}` ===
          documentToUpload
        ) {
          return {
            ...document,
            fileStatus: true,
          };
        }
        return document;
      });
      setSelectedRequest({
        ...selectedRequest,
        documents,
      });
      setOpenUploadDocumentPDF(false);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleDownloadDocumentPDF = async (documentId: string) => {
    const document = selectedRequest.documents.find(
      (x: any) => x.id === documentId,
    );
    const documentKey = `${document.businessIdentifier}-${document.folio}`;
    setLoading(true);
    try {
      const data = {
        requestId: documentKey,
        fileName: `${documentKey}.pdf`,
        requestType: 'document',
      };
      const response = await downloadFilesFromRequest(dispatch, data);
      if (response) {
        window.open(response);
      } else {
        showAlert(dispatch, 'error', 'No hay archivo para descargar');
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleApplyReplacementInvoice = async () => {
    setLoading(true);
    const documentId = selected[0];
    const document = selectedRequest.documents.find(
      (x: any) => x.id === documentId,
    );
    setDocumentToReplace(document);
    try {
      const response = await getReplacementInvoices(dispatch, documentId);
      if (response.length === 0) {
        showAlert(
          dispatch,
          'error',
          'No hay facturas de reemplazo disponibles',
        );
      } else {
        setReplacementInvoices(response);
        setOpenSelectReplacementInvoice(true);
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleOpenRequirementsDialog = (rowId: string) => {
    const document = selectedRequest.documents.find((x: any) => x.id === rowId);
    if (document) {
      setDocumentId(document.id);
      setDocumentAlias(`Folio ${document.folio}`);
      setOpenRequirementsDialog(true);
    }
  };

  const handleRequirements = {
    key: 'requirements',
    label: '',
    handleAction: handleOpenRequirementsDialog,
    type: 'requirements',
  };

  const canReplaceInvoice = validatePermissions(
    user,
    'financing_factoring_replace_invoice',
  );

  const fetchReplacementConfiguration = async () => {
    try {
      const replacementEnabledResponse = await getCompanyConfig(
        dispatch,
        'system_enable_invoice_replacement',
      );
      const configEnabled = replacementEnabledResponse === '1';
      setReplacementEnabled(configEnabled);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    void fetchReplacementConfiguration();
  }, []);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <div>
      <HeaderWithActions
        variant="body1"
        titleButtons={actionButtons}
        buttons={
          replacementEnabled && tableInView === 'documents'
            ? [
                {
                  name: 'Factura de reemplazo',
                  action: () => void handleApplyReplacementInvoice(),
                  color: 'secondary',
                  disabled: selected.length !== 1 || !canReplaceInvoice,
                },
              ]
            : []
        }
      />
      {tableInView === 'applied' && (
        <EnhancedTable
          dataHeaders={appliedHeaders}
          data={documentsApplied}
          rowsPerPageDefault={5}
          finalSumRow
          exportToFile="xlsx"
        />
      )}
      {tableInView === 'documents' && (
        <EnhancedTable
          dataHeaders={[
            // handleUploadDocumentPDFObject,
            // handlePreviewDocumentPDFObject,
            handleRequirements,
            ...documentssHeaders,
          ]}
          data={selectedRequest.documents}
          rowsPerPageDefault={5}
          selected={selected}
          setSelected={setSelected}
          finalSumRow
          exportToFile="xlsx"
          exportHeaders={documentssHeaders}
        />
      )}
      {tableInView === 'payers' && (
        <PayersConcentration selectedRequest={selectedRequest} />
      )}
      {openUploadDocumentPDF && (
        <Dialog
          open={openUploadDocumentPDF}
          onClose={() => setOpenUploadDocumentPDF(false)}
        >
          <DialogTitle id="form-dialog-title">Subir factura</DialogTitle>
          <DialogContent style={{ minHeight: 100, padding: 50 }}>
            <Uploader
              buttonText="Subir documento"
              handleFileChange={handleFileChange}
              uploadedFileName={uploadedFileName}
              setUploadedFileName={setUploadedFileName}
            />
          </DialogContent>
          <DialogActions>
            <CustomButton
              onClick={() => setOpenUploadDocumentPDF(false)}
              color="secondary"
            >
              Cancelar
            </CustomButton>
            <CustomButton onClick={uploadFiles} color="primary">
              Subir
            </CustomButton>
          </DialogActions>
        </Dialog>
      )}
      {!openUploadDocumentPDF &&
        ['documents', 'applied', 'payers'].includes(tableInView) && (
          <DialogActions>
            <CustomButton
              onClick={() => setOpenShoppingCart(false)}
              color="secondary"
            >
              Salir
            </CustomButton>
          </DialogActions>
        )}
      {openSelectReplacementInvoice && (
        <ReplacementDialog
          openSelectReplacementInvoice={openSelectReplacementInvoice}
          setOpenSelectReplacementInvoice={setOpenSelectReplacementInvoice}
          replacementInvoices={replacementInvoices}
          documentToReplace={documentToReplace}
          selectedRequest={selectedRequest}
          setUp={setUp}
        />
      )}

      <DocumentsPreviewer
        open={previewOpen}
        onClose={handleClosePreview}
        fileName={currentPreviewFile ?? ''}
        blobUrl={blobUrl}
        contentType={contentType}
        loading={previewLoading}
        error={previewError}
        onDownload={() => {
          if (currentPreviewFile) {
            const document = selectedRequest.documents.find(
              (x: any) =>
                `${x.businessIdentifier}-${x.folio}.pdf` === currentPreviewFile,
            );
            if (document) {
              void handleDownloadDocumentPDF(document.id);
            }
          }
        }}
        onError={(error) => setPreviewError(error)}
      />

      {openRequirementsDialog && (
        <RequirementsDialog
          open={openRequirementsDialog}
          onClose={() => setOpenRequirementsDialog(false)}
          category={'factoringDocument'}
          entityKey={'documentId'}
          entityId={documentId}
          entityAlias={documentAlias}
        />
      )}
    </div>
  );
};

export default DocumentsDetailsModule;
